import type { ComponentProps, MouseEvent } from 'react';

import {
  RICH_TEXT_IDLE_PLACEHOLDER_CLASS,
  RICH_TEXT_PROSE_CLASS,
} from '@/components/editor/inline-rich-text-surface';
import { usePreviewInternalLinkClick } from '@/components/editor/use-preview-internal-link-click';
import { useIsPageEditor } from '@/hooks/use-is-page-editor';
import { hasRichTextContent } from '@/lib/rich-text';
import { cn } from '@/lib/utils';

export interface RichTextPreviewProps extends ComponentProps<'div'> {
  value: string;
  placeholder?: string;
  headingsOnly?: boolean;
}

/**
 * Renders rich text content in preview mode (non-editable).
 * Used when editor code is not loaded (public pages) or as fallback.
 */
export function RichTextPreview({
  value,
  placeholder,
  headingsOnly = false,
  className,
  ...props
}: RichTextPreviewProps) {
  const isEditor = useIsPageEditor();
  const isEmpty = !hasRichTextContent(value);
  const resolvedClassName = cn(RICH_TEXT_PROSE_CLASS, 'tiptap', className);
  const handlePreviewLinkClick = usePreviewInternalLinkClick();

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    props.onClick?.(event);
    if (event.defaultPrevented || !(event.target instanceof Element)) return;

    // CMS canvas: block navigation so selection/edit isn't interrupted.
    if (isEditor) {
      if (event.target.closest('a[href]')) {
        event.preventDefault();
      }
      return;
    }

    handlePreviewLinkClick(event);
  };

  if (isEmpty) {
    if (placeholder) {
      type EmptyPlaceholderTag = 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
      const PlaceholderTag = (
        headingsOnly ? `h${value.match(/^\s*<h([1-6])\b/i)?.[1] ?? '1'}` : 'p'
      ) as EmptyPlaceholderTag;
      return (
        <div className={resolvedClassName} {...props}>
          <PlaceholderTag className={RICH_TEXT_IDLE_PLACEHOLDER_CLASS} aria-hidden="true">
            {placeholder}
          </PlaceholderTag>
        </div>
      );
    }
    return null;
  }

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: Delegated actions target native links, which provide keyboard activation.
    <div
      className={resolvedClassName}
      {...props}
      onClick={handleClick}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: Rich text content from editor
      // biome-ignore lint/style/useNamingConvention: React API requires __html
      dangerouslySetInnerHTML={{ __html: value }}
    />
  );
}
